import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home,
  Wrench,
  ChevronDown,
  ChevronUp,
  Key,
  Users,
  LogOut,
  MessageCircle,
  Camera,
  User,
} from "lucide-react";
import CreateService from "../Service/CreateService";
import ManageLocksmith from "../LockSmith/ManageLocksmith";
import SetCommission from "../Commission/Commission";
import ViewServices from "../Service/ViewService";
import ApproveService from "../Service/ApproveService";
import ViewMessages from "../ViewMessage/ViewMessages";
import ManageCarKeyDetails from "../Service/ManageCarKeyDetails";
import ManageSuggestedServices from "../Service/ManageSuggestedServices";
import HeroSection from "../ContentManagement/HeroSection";
import ServiceCards from "../ContentManagement/ServiceCards";
import LockSmithEditor from "../ContentManagement/LockSmithEditor";
import UnlockYourFutureEditor from "../ContentManagement/UnlockYourFutureEditor";
import ServicesEditor from "../ContentManagement/ServicesEditor";
import HowWeWorkEditor from "../ContentManagement/HowWeWorkEditor";
import LastSectionEditor from "../ContentManagement/LastSectionEditor";
import FooterEditor from "../ContentManagement/FooterEditor";
import CCTVPreRegistration from "../CCTV/CCTVPreRegistration";
import MyProfile from "../MyProfile/MyProfile";
import api from "../../api/api";
import { FaMoneyBillWave } from 'react-icons/fa';
import "./Dashboard.css";

const DashboardHome = function () {
  return <div>Welcome to Admin Dashboard</div>;
};

const menuItems = [
  {
    name: "Dashboard",
    icon: Home,
    component: <DashboardHome />,
  },
  {
    name: "My Profile",
    icon: User,
    component: <MyProfile />,
  },
  {
    name: "Manage Service",
    icon: Wrench,
    subMenu: [
      { name: "Create Service", component: <CreateService /> },
      { name: "View Services", component: <ViewServices /> },
      {
        name: "Approve Services",
        component: <ApproveService />,
      },
      {
        name: "Manage Car Key Details",
        component: <ManageCarKeyDetails />,
      },
      {
        name: "Manage Suggested Services",
        component: <ManageSuggestedServices />,
      },
    ],
  },
  {
    name: "Earnings Summary",
    icon: FaMoneyBillWave,
    component: React.createElement(React.lazy(() => import('./EarningsSummary'))),
  },
  {
    name: "Content Management",
    icon: Wrench,
    subMenu: [
      { name: "Hero Section", component: <HeroSection /> },
      { name: "Service Cards", component: <ServiceCards /> },
      {
        name: "Locksmith Section",
        component: <LockSmithEditor />,
      },
      {
        name: "Unlock Your Future",
        component: <UnlockYourFutureEditor />,
      },
      {
        name: "Services Section",
        component: <ServicesEditor />,
      },
      { name: "How We Work", component: <HowWeWorkEditor /> },
      {
        name: "Last Section",
        component: <LastSectionEditor />,
      },
      {
        name: "Footer Section",
        component: <FooterEditor />,
      },
    ],
  },
  {
    name: "Manage Locksmith",
    icon: Key,
    component: <ManageLocksmith />,
  },
  {
    name: "Set Commission",
    icon: Users,
    component: <SetCommission />,
  },
  {
    name: "View Messages",
    icon: MessageCircle,
    component: <ViewMessages />,
  },
  {
    name: "CCTV Pre-registration",
    icon: Camera,
    component: <CCTVPreRegistration />,
  },
];

function Dashboard() {
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [expandedMenu, setExpandedMenu] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    const userRole = localStorage.getItem("userRole");

    if (!accessToken || userRole !== "admin") {
      navigate("/");
    }
  }, [navigate]);

  const handleMenuClick = function (item) {
    if (item.subMenu) {
      setExpandedMenu(expandedMenu === item.name ? null : item.name);
    } else {
      setActiveTab(item.name);
    }
  };

  const handleLogout = async function () {
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken) {
      alert("No refresh token found. Please log in again.");
      navigate("/");
      return;
    }

    try {
      await api.post(
        "/logout/",
        { refresh: refreshToken },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      localStorage.clear();
      navigate("/");
    } catch (err) {
      console.error("Logout Error:", err);
      alert("Logout failed. Please try again.");
    }
  };

  const ActiveComponent = menuItems
    .flatMap((item) => item.subMenu || item)
    .find((subItem) => subItem.name === activeTab)?.component;

  return (
    <div className="container-fluid">
      <div className="row dashboard-container">
        {/* Sidebar */}
        <div className="col-md-3 col-lg-2 sidebar">
          <div className="brand">
            <img
              src="images/logo.webp"
              alt="LockQuick Logo"
              className="logo-img"
            />
          </div>
          <div className="menu">
            {menuItems.map(function (item) {
              return (
                <div key={item.name}>
                  <button
                    onClick={() => handleMenuClick(item)}
                    className={
                      "menu-item" + (activeTab === item.name ? " active" : "")
                    }
                  >
                    <item.icon size={18} /> {item.name}
                    {item.subMenu
                      ? expandedMenu === item.name
                        ? <ChevronUp size={16} />
                        : <ChevronDown size={16} />
                      : null}
                  </button>
                  {item.subMenu &&
                    expandedMenu === item.name &&
                    <div className="submenu">
                      {item.subMenu.map((subItem) => (
                        <button
                          key={subItem.name}
                          onClick={() => setActiveTab(subItem.name)}
                          className={
                            "submenu-item" +
                            (activeTab === subItem.name ? " active" : "")
                          }
                        >
                          {subItem.name}
                        </button>
                      ))}
                    </div>
                  }
                </div>
              );
            })}
          </div>
          <button
            onClick={handleLogout}
            className="logout-button"
          >
            <LogOut size={18} /> Logout
          </button>
        </div>
        {/* Main Content */}
        <div className="col-md-9 col-lg-10 main-content">
          <div className="card">
            <div className="card-body">
              {ActiveComponent}
            </div>
          </div>
          <div className="footer" />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
